import type {
  PatientOpenRedFlag,
  PatientRedFlagCalledRequest,
  PatientRedFlagsResponse,
} from "../../../shared/api-types";

// The patient page's red-flag banner (NO-850): what NO-846's
// GET /api/patients/:patientId/red-flags and its Mark called answer read as on
// the page. Pure and relative-import only, so `tsx --test` runs it.

export const RED_FLAG_READ_FAILED_MESSAGE = "Couldn't check for a red flag.";

export const MARK_CALLED_FAILED_MESSAGE = "Couldn't mark it called. Try again.";

export interface RedFlagBannerView {
  /** Every critical flag's headline, in Analysis order, without its end stop. */
  flagText: string;
  /** The appointment the flagged Session belongs to, when it has one. */
  appointmentId: string | null;
  /** Mark called names exactly the flag the RD was shown. */
  request: PatientRedFlagCalledRequest;
}

/** What the banner slot shows: nothing, the banner, or the failed-read line. */
export type RedFlagSectionState = "nothing" | "banner" | "failed";

/** What a Mark called answer means for the banner. */
export type MarkCalledOutcome = "cleared" | "refetch" | "failed";

/** The facts of a failed request the page can see; empty for a network failure. */
export interface RequestFailure {
  status?: number;
  body?: unknown;
}

export function patientRedFlagsQueryKey(patientId: string): string[] {
  return ["patient-red-flags", patientId];
}

/** Null (no banner, no placeholder) unless the Client has an open flag. */
export function redFlagBannerView(
  openFlag: PatientOpenRedFlag | null
): RedFlagBannerView | null {
  if (!openFlag || openFlag.flags.length === 0) return null;

  return {
    flagText: openFlag.flags.map(({ lede }) => withoutEndStop(lede)).join("; "),
    appointmentId: openFlag.appointmentId,
    request: {
      sessionId: openFlag.sessionId,
      analysisRevisionId: openFlag.analysisRevisionId,
      raisedAt: openFlag.raisedAt,
    },
  };
}

function withoutEndStop(lede: string): string {
  return lede.trim().replace(/[.!?]+$/, "");
}

/**
 * Loading and "no open flag" both show nothing. Any failed read shows a line,
 * so a read that failed never looks like a Client with no flag.
 */
export function redFlagSectionState(read: {
  isPending: boolean;
  isError: boolean;
  data: PatientRedFlagsResponse | undefined;
}): RedFlagSectionState {
  if (read.isError) return "failed";

  if (read.isPending || !read.data) return "nothing";

  return redFlagBannerView(read.data.openFlag) ? "banner" : "nothing";
}

/**
 * Success clears the banner. A 409 `red_flag_not_open` means the flag changed
 * since the page read it, so the page reads it again with no error. Anything
 * else keeps the banner and says the mark failed.
 */
export function markCalledOutcome(failure: RequestFailure | null): MarkCalledOutcome {
  if (failure === null) return "cleared";

  return isFlagNotOpen(failure) ? "refetch" : "failed";
}

function isFlagNotOpen({ status, body }: RequestFailure): boolean {
  if (status !== 409 || !body || typeof body !== "object") return false;

  return (body as { code?: unknown }).code === "red_flag_not_open";
}
